/**
 * 서비스 카테고리 상수 정의
 * 앱의 manifest.json에서 사용하는 카테고리와 일치해야 함
 */

// 앱 manifest.json의 category 필드 값 (영문)
export const SERVICE_CATEGORIES = [
  'ai',
  'connect',
  'transform',
  'trigger',
  'storage',
  'collect',
  'visualize',
] as const;

export type ServiceCategory = typeof SERVICE_CATEGORIES[number];

/**
 * 카테고리 한국어 라벨 매핑
 */
export const CATEGORY_LABELS: Record<ServiceCategory, string> = {
  'ai': 'AI 분석',
  'connect': '연결/통합',
  'transform': '변환/처리',
  'trigger': '트리거',
  'storage': '저장소',
  'collect': '수집',
  'visualize': '시각화',
};

/**
 * 카테고리별 아이콘 (이모지)
 * 워크플로우 빌더와 동일한 아이콘 사용
 */
export const CATEGORY_ICONS: Record<ServiceCategory, string> = {
  'trigger': '🎯',
  'ai': '🤖',
  'transform': '🔄',
  'connect': '🔗',
  'collect': '📥',
  'storage': '💾',
  'visualize': '📊',
};

/**
 * 카테고리별 기능 목록
 */
export const CATEGORY_FEATURES: Record<ServiceCategory, string[]> = {
  'ai': [
    'AI 챗봇',
    '텍스트 요약',
    '텍스트 분류',
    '정보 추출',
  ],
  'connect': [
    '이메일 발송',
    '이메일 수신',
    '웹 스크래핑',
    '웹훅 연동',
  ],
  'transform': [
    'OCR 텍스트 추출',
    'PDF 변환',
    'JSON 파싱',
  ],
  'trigger': [
    '파일 업로드',
    '폼 입력',
  ],
  'storage': [
    '데이터베이스 저장',
  ],
  'collect': [
    '설문 수집',
  ],
  'visualize': [
    '차트 생성',
    '윈도우 표시',
  ],
};

/**
 * 카테고리 검증 함수
 */
export function isValidCategory(category: string): category is ServiceCategory {
  return SERVICE_CATEGORIES.includes(category as ServiceCategory);
}

/**
 * 카테고리 한국어 라벨 조회
 */
export function getCategoryLabel(category: ServiceCategory): string {
  return CATEGORY_LABELS[category] || category;
}

/**
 * 카테고리 아이콘 조회
 */
export function getCategoryIcon(category: string | undefined): string {
  if (!category) return '🔧';
  return CATEGORY_ICONS[category as ServiceCategory] || '🔧';
}

/**
 * 서비스 아이콘 조회
 * 1. 서비스 메타데이터의 icon 필드 우선
 * 2. 없으면 카테고리 기본 아이콘 사용
 */
export function getServiceIcon(metadata?: Record<string, any>, category?: string): string {
  // 메타데이터에서 아이콘 확인
  if (metadata?.icon) {
    return metadata.icon;
  }
  // 카테고리 기반 기본 아이콘
  return getCategoryIcon(category);
}

/**
 * 카테고리별 기능 목록 조회
 */
export function getCategoryFeatures(category: ServiceCategory): string[] {
  return CATEGORY_FEATURES[category] || [];
}

/**
 * 프론트엔드용 카테고리 옵션 (Select 컴포넌트용)
 */
export const categoryOptions = SERVICE_CATEGORIES.map(category => ({
  value: category,
  label: CATEGORY_LABELS[category],
  icon: CATEGORY_ICONS[category],
  features: CATEGORY_FEATURES[category],
}));

